import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router';

import { getLibraryContentQuery } from '../queries/common';

import { slugSeparator } from '@cobalt/utilities';

export const useRoutedContent = <T extends { id: string } = any>(
  items: T[],
  getNewUrl: (item: T) => string,
  getLibraryPath: (item: T) => string,
  onContentLoaded: (content: string) => void,
  useRouting = true,
) => {
  const { id: sluggedId } = useParams();
  const id = useMemo(() => sluggedId?.split(slugSeparator)[0], [sluggedId]);
  const navigate = useNavigate();

  const [selectedItem, setSelectedItem] = useState<T>(null);

  const updateSelectedItem = useCallback(
    (newItem: T, replace?: boolean) => {
      if (!newItem) return;

      if (useRouting) {
        navigate(getNewUrl(newItem), { replace });
      } else {
        setSelectedItem(newItem);
      }
    },
    [getNewUrl, navigate, useRouting],
  );

  useEffect(() => {
    const activeItem = items?.find((item) => item.id === id);
    if (activeItem) {
      setSelectedItem(activeItem);
    } else if (useRouting) {
      updateSelectedItem(items?.[0], true);
    }
  }, [id, useRouting, items, updateSelectedItem]);

  const libraryPath = useMemo(() => selectedItem && getLibraryPath(selectedItem), [selectedItem, getLibraryPath]);

  const { data: content, isLoading } = useQuery({
    ...getLibraryContentQuery<string>(libraryPath, 'text'),
    enabled: !!libraryPath,
  });

  useEffect(() => {
    if (!onContentLoaded) return;
    if (isLoading) {
      onContentLoaded('<pk-spinner size="70"></pk-spinner>');
      return;
    }

    if (!content) return;

    onContentLoaded(content);
  }, [content, isLoading, onContentLoaded]);

  return { selectedItem, updateSelectedItem };
};
